@if (!isLoggedIn) {
  <div class="chat-guest">
    <mat-icon class="chat-guest-icon">forum</mat-icon>
    <h2>{{ 'chat.title' | t }}</h2>
    <p>{{ 'chat.loginRequired' | t }}</p>
    <button type="button" class="btn-pill btn-pill-accent" (click)="login()">{{ 'header.loginOrSignup' | t }}</button>
  </div>
} @else {
  <div class="chat-page" [class.has-conversation]="!!friendId">

    <!-- Colonne de gauche : discussions, demandes, recherche -->
    <aside class="chat-sidebar">
      <h2 class="chat-title">{{ 'chat.title' | t }}</h2>
      @if (showPushBanner) {
        <div class="chat-push-banner">
          <mat-icon>notifications_active</mat-icon>
          <span class="chat-push-text">{{ 'chat.pushBanner' | t }}</span>
          <span class="chat-actions">
            <button type="button" class="btn-pill btn-pill-accent" [disabled]="activatingPush" (click)="activatePush()">{{ 'chat.pushActivate' | t }}</button>
            <button type="button" class="btn-pill btn-pill-outline" (click)="dismissPushBanner()">{{ 'chat.pushLater' | t }}</button>
          </span>
        </div>
      }
      <div class="chat-tabs" role="tablist">
        <button type="button" role="tab" class="chat-tab" [class.active]="tab === 'chats'" [attr.aria-selected]="tab === 'chats'"
          (click)="setTab('chats')">{{ 'chat.tabChats' | t }}</button>
        <button type="button" role="tab" class="chat-tab" [class.active]="tab === 'requests'" [attr.aria-selected]="tab === 'requests'"
          (click)="setTab('requests')">
          {{ 'chat.tabRequests' | t }}
          @if (requestsCount > 0) {
            <span class="chat-count">{{ requestsCount }}</span>
          }
        </button>
        <button type="button" role="tab" class="chat-tab" [class.active]="tab === 'search'" [attr.aria-selected]="tab === 'search'"
          (click)="setTab('search')">
          <mat-icon>person_add</mat-icon>
          {{ 'chat.tabSearch' | t }}
        </button>
      </div>

      <div class="chat-sidebar-body">
        @switch (tab) {
          @case ('chats') {
            @for (item of friends; track item.user._id) {
              <a class="chat-row" [class.active]="item.user._id === friendId" [class.unread]="item.unread > 0"
                [routerLink]="['/messages', item.user._id]">
                <span class="chat-avatar">
                  <img [src]="avatar(item.user)" alt="" width="44" height="44" />
                  @if (item.online) {
                    <span class="chat-online" [attr.aria-label]="'chat.online' | t"></span>
                  }
                </span>
                <span class="chat-row-text">
                  <span class="chat-row-top">
                    <span class="chat-pseudo">{{ name(item.user) }}</span>
                    @if (item.lastMessage) {
                      <span class="chat-time">{{ shortTime(item.lastMessage.createdAt) }}</span>
                    }
                  </span>
                  <span class="chat-row-bottom">
                    <span class="chat-preview">{{ preview(item) }}</span>
                    @if (item.unread > 0) {
                      <span class="chat-count">{{ item.unread > 99 ? '99+' : item.unread }}</span>
                    }
                  </span>
                </span>
              </a>
            } @empty {
              <div class="chat-empty">
                <p>{{ 'chat.noFriends' | t }}</p>
                <button type="button" class="btn-pill btn-pill-outline" (click)="setTab('search')">
                  <mat-icon>person_add</mat-icon>{{ 'chat.findMembers' | t }}
                </button>
              </div>
            }
          }

          @case ('requests') {
            <h3 class="chat-section-title">{{ 'chat.received' | t }}</h3>
            @for (request of relations?.incoming; track request._id) {
              <div class="chat-row">
                <span class="chat-avatar"><img [src]="avatar(request.user)" alt="" width="44" height="44" /></span>
                <span class="chat-row-text">
                  <span class="chat-pseudo">{{ name(request.user) }}</span>
                  <span class="chat-actions">
                    <button type="button" class="btn-pill btn-pill-accent" [disabled]="isBusy(request._id)"
                      (click)="acceptRequest(request._id, request.user)">{{ 'chat.accept' | t }}</button>
                    <button type="button" class="btn-pill btn-pill-outline" [disabled]="isBusy(request._id)"
                      (click)="deleteRequest(request._id)">{{ 'chat.decline' | t }}</button>
                  </span>
                </span>
              </div>
            } @empty {
              <p class="chat-muted">{{ 'chat.noReceived' | t }}</p>
            }

            <h3 class="chat-section-title">{{ 'chat.sent' | t }}</h3>
            @for (request of relations?.outgoing; track request._id) {
              <div class="chat-row">
                <span class="chat-avatar"><img [src]="avatar(request.user)" alt="" width="44" height="44" /></span>
                <span class="chat-row-text">
                  <span class="chat-pseudo">{{ name(request.user) }}</span>
                  <span class="chat-actions">
                    <span class="chat-muted">{{ 'chat.pending' | t }}</span>
                    <button type="button" class="btn-pill btn-pill-outline" [disabled]="isBusy(request._id)"
                      (click)="deleteRequest(request._id)">{{ 'chat.cancelRequest' | t }}</button>
                  </span>
                </span>
              </div>
            } @empty {
              <p class="chat-muted">{{ 'chat.noSent' | t }}</p>
            }
          }

          @case ('search') {
            <div class="chat-search">
              <mat-icon>search</mat-icon>
              <input type="search" [(ngModel)]="searchText" (input)="onSearchInput()" [placeholder]="'chat.searchPlaceholder' | t"
                [attr.aria-label]="'chat.searchPlaceholder' | t" autocomplete="off" />
            </div>
            @if (searching) {
              <p class="chat-muted">{{ 'chat.searching' | t }}</p>
            }
            @for (result of searchResults; track result.user._id) {
              <div class="chat-row">
                <span class="chat-avatar"><img [src]="avatar(result.user)" alt="" width="44" height="44" /></span>
                <span class="chat-row-text">
                  <span class="chat-pseudo">{{ name(result.user) }}</span>
                  @if (name(result.user) !== result.user.pseudo) {
                    <span class="chat-muted chat-handle">&#64;{{ result.user.pseudo }}</span>
                  }
                  <span class="chat-actions">
                    @switch (result.relation) {
                      @case ('friend') {
                        <a class="btn-pill btn-pill-accent" [routerLink]="['/messages', result.user._id]">{{ 'chat.sendMessage' | t }}</a>
                      }
                      @case ('incoming') {
                        <button type="button" class="btn-pill btn-pill-accent" [disabled]="isBusy(result.requestId!)"
                          (click)="acceptRequest(result.requestId!, result.user, result)">{{ 'chat.accept' | t }}</button>
                      }
                      @case ('outgoing') {
                        <span class="chat-muted">{{ 'chat.pending' | t }}</span>
                        <button type="button" class="btn-pill btn-pill-outline" [disabled]="isBusy(result.requestId!)"
                          (click)="deleteRequest(result.requestId!, result)">{{ 'chat.cancelRequest' | t }}</button>
                      }
                      @default {
                        <button type="button" class="btn-pill btn-pill-accent" [disabled]="isBusy(result.user._id)"
                          (click)="sendRequest(result)">
                          <mat-icon>person_add</mat-icon>{{ 'chat.addFriend' | t }}
                        </button>
                      }
                    }
                  </span>
                </span>
              </div>
            } @empty {
              @if (!searching && searchText.trim().length >= 2) {
                <p class="chat-muted">{{ 'chat.noResults' | t }}</p>
              } @else if (!searching) {
                <p class="chat-muted">{{ 'chat.searchHint' | t }}</p>
              }
            }
          }
        }
      </div>
    </aside>

    <!-- Colonne de droite : conversation -->
    <section class="chat-conversation">
      @if (friendId && friend) {
        <header class="conv-header">
          <a class="conv-back" routerLink="/messages" [attr.aria-label]="'common.back' | t">
            <mat-icon>arrow_back</mat-icon>
          </a>
          <span class="chat-avatar">
            <img [src]="avatar(friend.user)" alt="" width="40" height="40" />
            @if (friend.online) {
              <span class="chat-online"></span>
            }
          </span>
          <span class="conv-title">
            <span class="chat-pseudo">{{ name(friend.user) }}</span>
            <span class="conv-status" [class.typing]="friendTyping">
              {{ (friendTyping ? 'chat.typing' : friend.online ? 'chat.online' : 'chat.offline') | t }}
            </span>
          </span>
          <button type="button" class="conv-menu" (click)="startCall(false)" [disabled]="!canCall"
            [attr.aria-label]="'call.audioCall' | t" [title]="'call.audioCall' | t">
            <mat-icon>call</mat-icon>
          </button>
          <button type="button" class="conv-menu" (click)="startCall(true)" [disabled]="!canCall"
            [attr.aria-label]="'call.videoCall' | t" [title]="'call.videoCall' | t">
            <mat-icon>videocam</mat-icon>
          </button>
          <button type="button" class="conv-menu" [matMenuTriggerFor]="convMenu" [attr.aria-label]="'chat.options' | t">
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #convMenu="matMenu" xPosition="before">
            <button mat-menu-item (click)="removeFriend()">
              <mat-icon>person_remove</mat-icon>
              <span>{{ 'chat.removeFriend' | t }}</span>
            </button>
          </mat-menu>
        </header>

        <div class="conv-messages" #messageList>
          @if (hasMore) {
            <button type="button" class="conv-older" [disabled]="loadingOlder" (click)="loadOlder()">{{ 'chat.loadOlder' | t }}</button>
          }
          @if (loadingMessages) {
            <p class="chat-muted conv-center">{{ 'chat.loading' | t }}</p>
          } @else if (messages.length === 0) {
            <div class="conv-center conv-empty">
              <img [src]="avatar(friend.user)" alt="" width="72" height="72" />
              <p>{{ 'chat.emptyConversation' | t: { pseudo: name(friend.user) } }}</p>
            </div>
          }
          @for (message of messages; track message._id; let i = $index) {
            @if (showDaySeparator(i)) {
              <div class="conv-day"><span>{{ dayLabel(message.createdAt) }}</span></div>
            }
            @if (showSender(i)) {
              <div class="conv-sender" [class.mine]="isMine(message)">{{ senderName(message) }}</div>
            }
            <div class="conv-bubble-row" [class.mine]="isMine(message)">
              <div class="conv-bubble" [title]="messageTime(message.createdAt)">
                <span class="conv-text">{{ message.text }}</span>
                <span class="conv-time">{{ messageTime(message.createdAt) }}</span>
              </div>
            </div>
            @if (isLastReadMine(i)) {
              <div class="conv-seen">{{ 'chat.seen' | t }}</div>
            }
          }
          @if (friendTyping) {
            <div class="conv-bubble-row">
              <div class="conv-bubble conv-typing" [attr.aria-label]="'chat.typing' | t"><span></span><span></span><span></span></div>
            </div>
          }
        </div>

        <form class="conv-composer" (ngSubmit)="send()">
          <textarea name="message" rows="1" maxlength="2000" [(ngModel)]="newMessage" (keydown)="onComposerKeydown($event)"
            (input)="onComposerInput()" [placeholder]="'chat.messagePlaceholder' | t" [attr.aria-label]="'chat.messagePlaceholder' | t"></textarea>
          <button type="submit" class="conv-send" [disabled]="sending || !newMessage.trim()" [attr.aria-label]="'chat.send' | t">
            <mat-icon>send</mat-icon>
          </button>
        </form>
      } @else {
        <div class="conv-center conv-placeholder">
          <mat-icon>forum</mat-icon>
          <p>{{ 'chat.selectConversation' | t }}</p>
        </div>
      }
    </section>
  </div>
}
